import React, { Fragment, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { usePopper } from 'react-popper';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';

import type { MainMenuItemPropsType } from '@/components/composite/Menu/MenuMain';
import type { SettingsMenuUserAvatar } from '@/components/composite/Menu/MenuSettings';

type SeparatorPropsType = Omit<ComponentPropsWithoutRef<'hr'>, 'className'> & {
    className?: string;
};

const Separator = ({ className, ...restProps }: SeparatorPropsType) => {
    return (
        <div className={clsx('py-1.5', className)}>
            <hr
                className="border-t-1 w-full border-icoGray-300 dark:border-icoDarkMode-beton"
                {...restProps}
            />
        </div>
    );
};

const Item = ({ className, ...restProps }: ComponentPropsWithoutRef<'div'>) => {
    return <div className={clsx('px-5 text-sm leading-tight', className)} {...restProps} />;
};

type MenuPopoverPropsType = ComponentPropsWithoutRef<'div'> & {
    panel?: ReactNode;
    panelClassName?: string;
    options?: object;
};

const MenuPopover = ({
    children,
    panel,
    options,
    panelClassName = '',
    ...restProps
}: MenuPopoverPropsType) => {
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
    const [popperElement, setPopperElement] = useState<HTMLDivElement>();
    const { styles, attributes } = usePopper(
        referenceElement,
        popperElement,
        options ?? {
            placement: 'right-start',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [-25, 20]
                    }
                }
            ]
        }
    );

    return (
        <Popover className="w-full" {...restProps}>
            {({ open }) => (
                <>
                    <Popover.Button ref={setReferenceElement} className="w-full" as="div">
                        {React.Children.map(children, (child) => {
                            if (child) {
                                if (typeof child['type'] === 'string') {
                                    return child;
                                }
                                const node = child as React.ReactElement<
                                    MainMenuItemPropsType | SettingsMenuUserAvatar
                                >;

                                return React.cloneElement(node, {
                                    open
                                });
                            }
                            return null;
                        })}
                    </Popover.Button>
                    <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                        <Transition
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1">
                            <Popover.Panel>
                                <div
                                    className={`w-56 rounded-md border border-white bg-white/80 font-GSemiBold shadow-icoXL backdrop-blur-sm firefox:bg-white firefox:bg-opacity-95 dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-deepBlack/80 dark:firefox:bg-icoDarkMode-deepBlack dark:firefox:bg-opacity-95 ${panelClassName}`}>
                                    {panel}
                                </div>
                            </Popover.Panel>
                        </Transition>
                    </div>
                </>
            )}
        </Popover>
    );
};

MenuPopover.Separator = Separator;
MenuPopover.Item = Item;

export default MenuPopover;
